export type ThemeMode = 'light' | 'dark' | 'sunset' | 'system'

const STORAGE_KEY = 'pds_theme'

function getSystemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false
}

export function getStoredTheme(): ThemeMode | null {
  const raw = localStorage.getItem(STORAGE_KEY)
  if (raw === 'light' || raw === 'dark' || raw === 'sunset' || raw === 'system') return raw
  return null
}

function resolveAutoTheme(now = new Date()): 'light' | 'dark' | 'sunset' {
  const h = now.getHours()
  // Heurystyka:
  // - 06:00–17:00: jasny
  // - 17:00–20:00: zachód słońca
  // - 20:00–06:00: ciemny
  if (h >= 6 && h < 17) return 'light'
  if (h >= 17 && h < 20) return 'sunset'
  return 'dark'
}

export function getMsUntilNextAutoThemeChange(now = new Date()): number {
  const next = new Date(now)
  const h = now.getHours()
  // boundary hours: 06:00, 17:00, 20:00
  if (h < 6) {
    next.setHours(6, 0, 0, 0)
  } else if (h < 17) {
    next.setHours(17, 0, 0, 0)
  } else if (h < 20) {
    next.setHours(20, 0, 0, 0)
  } else {
    next.setDate(next.getDate() + 1)
    next.setHours(6, 0, 0, 0)
  }
  return Math.max(1000, next.getTime() - now.getTime())
}

export function resolveTheme(mode: ThemeMode | null): 'light' | 'dark' | 'sunset' {
  // Default: auto-by-time when no override is stored.
  if (mode === null) return resolveAutoTheme()
  if (mode === 'system') return getSystemPrefersDark() ? 'dark' : 'light'
  return mode
}

export function applyTheme(mode: ThemeMode | null) {
  const resolved = resolveTheme(mode)
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.classList.toggle('theme-sunset', resolved === 'sunset')

  // Trigger a short "refresh" animation on theme changes.
  try {
    const w = window as unknown as {
      __pdsThemeAnimTimeout?: number
      __pdsSunsetAnimTimeout?: number
      __pdsResolvedTheme?: 'light' | 'dark' | 'sunset'
      __pdsSunsetScrollHandler?: () => void
      __pdsSunsetScrollRaf?: number
    }
    const prevResolved = w.__pdsResolvedTheme
    w.__pdsResolvedTheme = resolved
    const didResolvedChange = prevResolved !== undefined && prevResolved !== resolved

    if (didResolvedChange) {
      if (w.__pdsThemeAnimTimeout) window.clearTimeout(w.__pdsThemeAnimTimeout)
      root.classList.remove('pds-theme-changing')
      // Force reflow so re-adding the class reliably restarts animation.
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      root.offsetHeight
      root.classList.add('pds-theme-changing')
      w.__pdsThemeAnimTimeout = window.setTimeout(() => {
        root.classList.remove('pds-theme-changing')
      }, 900)
    }

    // Extra animation when entering "sunset".
    if (didResolvedChange && prevResolved && prevResolved !== 'sunset' && resolved === 'sunset') {
      if (w.__pdsSunsetAnimTimeout) window.clearTimeout(w.__pdsSunsetAnimTimeout)
      root.classList.remove('pds-sunset-enter')
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      root.offsetHeight
      root.classList.add('pds-sunset-enter')
      w.__pdsSunsetAnimTimeout = window.setTimeout(() => {
        root.classList.remove('pds-sunset-enter')
      }, 3200)
    }

    // Subtle "sunset light" parallax on scroll (only in sunset mode).
    const reduceMotion =
      window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? false

    if (!reduceMotion && resolved === 'sunset') {
      if (!w.__pdsSunsetScrollHandler) {
        w.__pdsSunsetScrollHandler = () => {
          if (w.__pdsSunsetScrollRaf) return
          w.__pdsSunsetScrollRaf = window.requestAnimationFrame(() => {
            w.__pdsSunsetScrollRaf = undefined
            const y = window.scrollY || 0
            const parallax = Math.round(y * 0.12) // px
            root.style.setProperty('--pds-sunset-parallax', `${parallax}px`)
          })
        }
        window.addEventListener('scroll', w.__pdsSunsetScrollHandler, {
          passive: true,
        })
      }
      w.__pdsSunsetScrollHandler()
    } else {
      if (w.__pdsSunsetScrollHandler) {
        window.removeEventListener('scroll', w.__pdsSunsetScrollHandler)
        w.__pdsSunsetScrollHandler = undefined
      }
      if (w.__pdsSunsetScrollRaf) {
        window.cancelAnimationFrame(w.__pdsSunsetScrollRaf)
        w.__pdsSunsetScrollRaf = undefined
      }
      root.style.removeProperty('--pds-sunset-parallax')
    }
  } catch {
    // ignore
  }
}

export function setTheme(mode: ThemeMode) {
  localStorage.setItem(STORAGE_KEY, mode)
  applyTheme(mode)
}

export function clearThemeOverride() {
  localStorage.removeItem(STORAGE_KEY)
  applyTheme(null)
}

export function initTheme() {
  applyTheme(getStoredTheme())
}

